import { Outlet, useParams } from 'react-router-dom'

import { Box, Button, Tag, Text } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  useTrainingMembershipsDataTable,
} from 'generated/resources/datatable.graphql'
import { ExportTrainingsTeamFragment, TrainingDetailFragment } from 'generated/resources/graphql'
import { TrainingClassFragment } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { classFilter } from 'modules/trainings/components/Revamp/TrainingsDetail/TrainingEmployeesDataTable/Filters/ClassFilter'
import { teamsFilter } from 'modules/trainings/components/Revamp/TrainingsDetail/TrainingEmployeesDataTable/Filters/TeamsFilter'
import { trainingStatusFilter } from 'modules/trainings/components/Revamp/TrainingsDetail/TrainingEmployeesDataTable/Filters/TrainingStatusFilter'
import { TrainingMembershipStatusTag } from 'modules/trainings/components/Shared/TrainingMembershipStatusTag'

import { AddParticipantsRow } from './AddParticipantsRow'
import { CertificateProjection } from './CertificateProjection'
import CompletionTag from './CompletionTag'
import { EnrolledEmployees } from './enrolledEmployees'

export const TrainingEmployeesDataTable = ({
  training,
  trainingClasses,
  teams,
}: {
  training: TrainingDetailFragment
  trainingClasses: TrainingClassFragment[]
  teams: ExportTrainingsTeamFragment[]
}) => {
  const { id } = useParams()
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const trainingClassesPath = (accessId: number) => {
    if (trainingClasses.length !== 0) {
      return navigation.trainings.list.details_revamp.view.participants.assignParticipant.fullPath({
        id: Number(id),
        accessId: accessId,
      })
    }

    return navigation.trainings.list.details_revamp.view.trainingClasses.tab.newClass.fullPath({
      id: Number(id),
    })
  }

  const { navigation } = useNavigation()

  const source = useTrainingMembershipsDataTable(
    [
      {
        identifier: 'name',
        title: i18n.t('trainings.trainings_revamp.training_detail.employees_table.reviewed_name'),
        kind: ProjectionKind.Custom,
        template: '',
        colSpan: 3,
        render: ({ access }) => {
          return <EnrolledEmployees access={access} />
        },
      },
      {
        identifier: 'status',
        sortBy: 'status',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.trainings_revamp.training_detail.employees_table.reviewed_status'),
        colSpan: 2,
        render: ({ status }) => {
          return <TrainingMembershipStatusTag status={status} />
        },
        template: '',
      },
      {
        identifier: 'certificate',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.trainings_revamp.training_detail.employees_table.certificate'),
        colSpan: 2,
        render: ({ trainingMembershipFiles }) => {
          return trainingMembershipFiles.length ? (
            <CertificateProjection
              trainingMembershipFiles={trainingMembershipFiles}
              trainingId={training.id}
            />
          ) : (
            <Text size="200"> - </Text>
          )
        },
        template: '',
      },
      {
        identifier: 'completedAt',
        kind: ProjectionKind.Custom,
        hidden: training?.validFor ? false : true,
        title: i18n.t('trainings.trainings_revamp.training_detail.employees_table.completion_date'),
        colSpan: 2,
        render: ({ trainingCompletedAt }) => {
          if (!trainingCompletedAt)
            return (
              <Text.Normal>
                {i18n.t(
                  'trainings.trainings_revamp.training_detail.employees_table.empty_completion_date'
                )}
              </Text.Normal>
            )

          const dateOptions: Intl.DateTimeFormatOptions = {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            timeZone: 'UTC',
          }
          const trainingDate = new Date(trainingCompletedAt)
          const formatedDate = new Intl.DateTimeFormat(undefined, dateOptions).format(trainingDate)
          return <Text.Normal>{formatedDate}</Text.Normal>
        },
        template: '',
      },
      {
        identifier: 'dueDate',
        sortBy: 'trainingDueDate',
        hidden: training?.validFor ? false : true,
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.trainings_revamp.training_detail.employees_table.due_date.title'),
        colSpan: 2,
        render: ({ trainingDueDate }) => {
          if (!trainingDueDate)
            return (
              <Tag type="transparent">
                {i18n.t(
                  'trainings.trainings_revamp.training_detail.employees_table.empty_due_date'
                )}
              </Tag>
            )
          return <CompletionTag dueDate={trainingDueDate} />
        },
        template: '',
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        render: ({ id: membershipId, accessId, status }) => {
          return (
            <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
              <PopoverMenu type="list">
                <PopoverMenu.Trigger>
                  <Button.Decorative type="secondary" iconName="ellipsis"></Button.Decorative>
                </PopoverMenu.Trigger>
                <PopoverMenu.Content side="bottom" align="center">
                  <PopoverMenu.RouterOption
                    title={i18n.t(
                      'trainings.trainings_revamp.training_detail.employees_table.dropdown.assign_participants'
                    )}
                    to={{
                      pathname: trainingClassesPath(accessId),
                    }}
                    icon="user"
                  />
                  {training?.validFor && (
                    <PopoverMenu.RouterOption
                      title={i18n.t(
                        'trainings.trainings_revamp.training_detail.employees_table.update_completed_date_modal.title'
                      )}
                      to={{
                        pathname:
                          navigation.trainings.list.details_revamp.view.participants.updateCompletionDate.fullPath(
                            { id: Number(id), membershipId: membershipId }
                          ),
                      }}
                      icon="pencil"
                      disabled={status !== 'completed'}
                    />
                  )}
                  <PopoverMenu.RouterOption
                    title={i18n.t(
                      'trainings.trainings_revamp.training_detail.employees_table.dropdown.delete_participants'
                    )}
                    to={{
                      pathname:
                        navigation.trainings.list.details_revamp.view.participants.deleteParticipant.fullPath(
                          { id: Number(id), membershipId: membershipId }
                        ),
                    }}
                    danger
                    icon="delete"
                  />
                </PopoverMenu.Content>
              </PopoverMenu>
              <Button.Router
                to={navigation.trainings.list.details_revamp.view.participants.employees.fullPath({
                  id: training.id,
                  employeeId: accessId,
                })}
                type="secondary"
                iconName="arrow-right"
              />
            </Box>
          )
        },
      },
    ],
    {
      variables: { trainingId: training.id, search: debouncedSearch },
    }
  )

  const filters = [
    classFilter(source, trainingClasses),
    trainingStatusFilter(source),
    teamsFilter(source, teams),
  ]

  return (
    <Box gap="s24">
      <DataTable.Provider
        source={source}
        pagination={{
          kind: 'offset',
          itemsPerPage: 50,
        }}
        filters={filters}
      >
        <DataTable.HeadBar
          disableExport
          search={{
            placeholder: i18n.t('common.search'),
            value: search,
            onChange: onSearch,
          }}
          exportFileName={i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.export'
          )}
          disableEditColumns
          actions={<AddParticipantsRow emptyClasses={trainingClasses.length === 0} />}
        />
        <DataTable.Table selectable={() => true} />
        <Outlet />
      </DataTable.Provider>
    </Box>
  )
}
